import React from 'react';
import { motion } from 'framer-motion';
import type { Slide } from './slides';
const ease = [0.22, 1, 0.36, 1] as const;
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 18
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease
    }
  }
};
function Eyebrow({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const Icon = slide.icon;
  return <motion.div variants={item} className="flex items-center gap-3 text-accent" data-id="element-87" data-mp="2orgbksviwjzb">
      <span className="flex h-8 w-8 items-center justify-center rounded-md border border-accent/25 bg-accent/10" data-id="element-88" data-mp="2ko0fpyra2nno">
        <Icon className="h-4 w-4" strokeWidth={2.2} data-id="element-89" data-mp="2fonjwoifi68y" />
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.25em]" data-id="element-90" data-mp="199d5dgaouj6p">
        {String(index + 1).padStart(2, '0')} · {slide.section}
      </span>
    </motion.div>;
}
export function SlideView({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const isTitle = slide.kind === 'title';
  return <motion.div key={slide.id} variants={container} initial="hidden" animate="show" exit={{
    opacity: 0,
    y: -12,
    transition: {
      duration: 0.25,
      ease
    }
  }} className="mx-auto flex w-full max-w-5xl flex-col justify-center px-6 py-16 sm:px-10 md:px-14" data-id="element-91" data-mp="1n4h4vc4mdvq8">
      {!slide.moatStages && !slide.velocityCards && <Eyebrow slide={slide} index={index} data-id="element-92" data-mp="zed2t972y41c" />}

      <div className={`${slide.moatStages || slide.velocityCards ? '' : 'mt-8'} ${slide.portrait ? 'grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_270px]' : ''}`} data-id="element-93" data-mp="zdbu3bdjdtms">
        <div data-id="element-94" data-mp="qgb7naa125lz">
          {!slide.moatStages && !slide.velocityCards && <motion.h1 variants={item} className={`font-extrabold tracking-tight text-white ${isTitle ? 'text-5xl leading-[1.05] sm:text-6xl md:text-7xl' : 'text-4xl leading-[1.08] sm:text-5xl md:text-6xl'}`} data-id="element-95" data-mp="uh3gw3iwgai9">
              {slide.title}
            </motion.h1>}

          {slide.lead && <motion.p variants={item} className="mt-6 max-w-3xl text-lg font-medium text-accent/90 sm:text-xl" data-id="element-96" data-mp="2n6dx0e4ngxd7">
              {slide.lead}
            </motion.p>}

          {slide.body.length > 0 && <motion.div variants={item} className="mt-6 max-w-3xl space-y-4 text-lg leading-relaxed text-slate-400 sm:text-xl" data-id="element-97" data-mp="29e7g2ptoamcp">
              {slide.body.map((p, i) => <p key={i} data-id="element-98" data-mp="322bovk7jjfjm" data-mpi={i}>{p}</p>)}
            </motion.div>}

          {slide.metrics && !slide.competitiveTableRows && <motion.div variants={item} className={`overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.06] ${slide.kind === 'appendix' ? 'mt-10 grid max-w-3xl grid-cols-1 gap-px sm:grid-cols-2' : 'mt-12 grid grid-cols-2 gap-px sm:grid-cols-4'}`} data-id="element-99" data-mp="2s3w7i8ybfknb">
              {slide.metrics.map(m => <div key={m.label} className={`bg-panel2 ${slide.kind === 'appendix' ? 'px-5 py-5 sm:px-6' : 'px-5 py-7 sm:px-6 sm:py-8'}`} data-id="element-100" data-mp="1bj3of035pvh2">
                  {slide.kind === 'appendix' ? <>
                      <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-accent" data-id="element-101" data-mp="1pa06hwzgiu9v">
                        {m.label}
                      </div>
                      {m.href ? <a href={m.href} target={m.href.startsWith('http') ? '_blank' : undefined} rel={m.href.startsWith('http') ? 'noreferrer' : undefined} className="mt-2 block break-words text-base font-semibold leading-snug text-white underline decoration-accent/50 underline-offset-4 transition-colors hover:text-accent hover:decoration-accent sm:text-lg" data-id="element-102" data-mp="1za967ksnqxu0">
                          {m.value}
                        </a> : <div className="mt-2 break-words text-base font-semibold leading-snug text-white sm:text-lg" data-id="element-103" data-mp="29cl99ytjqpf2">
                          {m.value}
                        </div>}
                    </> : <>
                      <div className="font-mono text-2xl font-semibold text-accent sm:text-3xl" data-id="element-104" data-mp="1clsur5m9h5fe">
                        {m.value}
                      </div>
                      <div className="mt-2 text-sm leading-snug text-slate-400" data-id="element-105" data-mp="1qes5yvf0gdh5">
                        {m.label}
                      </div>
                    </>}
                </div>)}
            </motion.div>}

          {slide.mechanisms && <motion.div variants={item} className="mt-9 grid gap-3 md:grid-cols-3" aria-label="Defensible serving mechanisms" data-id="element-106" data-mp="sx1sow54umpi">
              {slide.mechanisms.map((mechanism, mechanismIndex) => <article key={mechanism.title} className="rounded-lg border border-white/[0.08] bg-panel2 p-4" data-id="element-107" data-mp="2vkqr3q80u359" data-mpi={mechanismIndex}>
                  <span className="font-mono text-xs font-semibold text-accent" data-id="element-108" data-mp="30hh424oj4img">
                    0{mechanismIndex + 1}
                  </span>
                  <h2 className="mt-2 text-sm font-bold text-white" data-id="element-109" data-mp="3sb12d2u888ni">
                    {mechanism.title}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400" data-id="element-110" data-mp="3eagj65kegath">
                    {mechanism.body}
                  </p>
                </article>)}
            </motion.div>}

          {slide.moatStages && <motion.section variants={item} className="mt-1" aria-label="OpenFPGA model and silicon pipeline" data-id="element-111" data-mp="27umnhiuu23e9">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-[#f5bc45]" data-id="element-112" data-mp="1ntb9i7ozsnwp">The moat</p>
              <h1 className="mt-4 max-w-6xl text-4xl font-extrabold leading-[1.04] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl" data-id="element-113" data-mp="1js98pmffg3sm">
                {slide.moatHeadline}
              </h1>
              {slide.moatDescription && <p className="mt-6 max-w-5xl text-lg leading-relaxed text-slate-400 sm:text-xl" data-id="element-114" data-mp="19zak44jxkd90">
                  {slide.moatDescription}
                </p>}

              <div className="mt-9 grid gap-3 lg:grid-cols-[minmax(0,1fr)_28px_minmax(0,1fr)_28px_minmax(0,1fr)_28px_minmax(0,1fr)]" data-id="element-115" data-mp="102fp3vmv8uzh">
                {slide.moatStages.map((stage, stageIndex) => <React.Fragment key={stage.number} data-id="element-116" data-mp="r3u5dx5nr9x4" data-mpi={stageIndex}>
                    <article className={`min-h-[190px] rounded-2xl border bg-panel2 p-5 ${stageIndex === slide.moatStages!.length - 1 ? 'border-[#f5bc45]/80 bg-[#f5bc45]/[0.06]' : 'border-slate-700/80'}`} data-id="element-117" data-mp="2qfd65ogt5bvp">
                      <div className="font-mono text-xs font-medium tracking-[0.16em] text-[#f5bc45]" data-id="element-118" data-mp="2t0kwuxvsvd9t">{stage.number} · {stage.title}</div>
                      <h2 className="mt-5 text-xl font-bold leading-tight text-slate-100 sm:text-2xl" data-id="element-119" data-mp="2xxl2x8sngg0x">{stage.detail.split('\n')[0]}</h2>
                      {stage.detail.split('\n')[1] && <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-120" data-mp="37nnv56ry5py7">{stage.detail.split('\n')[1]}</p>}
                    </article>
                    {stageIndex < slide.moatStages!.length - 1 && <span aria-hidden className="hidden self-center text-center text-2xl font-semibold text-[#d99a37] lg:block" data-id="element-121" data-mp="2jzedmh1rnocv">→</span>}
                  </React.Fragment>)}
              </div>

              {slide.moatStats && <div className="mt-7 flex flex-wrap gap-3 font-mono text-sm leading-relaxed text-slate-200 sm:text-base" data-id="element-122" data-mp="3vea3kz5v5c8q">
                  {slide.moatStats.map((stat, statIndex) => <span key={stat.value} className="rounded-full border border-slate-700 bg-panel2 px-4 py-2.5" data-id="element-123" data-mp="pcsuk3rys65i" data-mpi={statIndex}>
                      <span className={statIndex === 2 ? 'text-slate-200' : 'font-semibold text-[#f5bc45]'} data-id="element-124" data-mp="kb3jwp8nluhy">{stat.value}</span>{' '}
                      <span className={statIndex === 2 || statIndex === 3 ? 'text-[#f5bc45] font-semibold' : ''} data-id="element-125" data-mp="ubnnhyjnbb3c">{stat.label}</span>
                    </span>)}
                </div>}

              {slide.moatConclusion && <p className="mt-8 rounded-2xl border border-slate-700 border-l-4 border-l-[#f5bc45] bg-[#202938] px-6 py-7 text-xl font-bold leading-snug text-slate-100 sm:text-2xl" data-id="element-126" data-mp="133fx670uig6v">
                  {slide.moatConclusion}
                </p>}

              {slide.proof && <footer className="mt-8 flex flex-col gap-3 border-t border-slate-700 pt-5 font-mono text-sm sm:flex-row sm:items-center sm:justify-between sm:text-base" data-id="element-127" data-mp="3jslcxaxf0oas">
                  <span className="font-semibold text-[#f5bc45]" data-id="element-128" data-mp="1uc8ze191ncib">OpenFPGA.ai</span>
                  <span className="text-slate-400" data-id="element-129" data-mp="1zasm43vgosbq">{slide.proof}</span>
                </footer>}
            </motion.section>}

          {slide.velocityCards && <motion.section variants={item} className="mt-1" aria-label="Thirty-day velocity" data-id="element-130" data-mp="1pk7skuunadzo">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-[#f5bc45]" data-id="element-131" data-mp="1bkybzu2j1hv6">
                {slide.velocityHeadline}
              </p>
              <h1 className="mt-4 text-4xl font-extrabold leading-[1.04] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl" data-id="element-132" data-mp="1fnmnyamjf4wd">
                30-Day Velocity
              </h1>
              {slide.velocityDescription && <p className="mt-6 max-w-5xl text-lg leading-relaxed text-slate-400 sm:text-xl" data-id="element-133" data-mp="2w0jrwz97e1wu">
                  {slide.velocityDescription}
                </p>}

              <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-id="element-134" data-mp="30xjydb9yt6oh">
                {slide.velocityCards.map(card => <article key={card.value} className={`min-h-[190px] rounded-2xl border border-slate-700/80 bg-panel2 p-5 ${card.className ?? ''}`} data-id="element-135" data-mp="349rmgqfxip7f">
                    <p className="font-mono text-4xl font-semibold tracking-tight text-[#f5bc45] sm:text-5xl" data-id="element-136" data-mp="2vgwn2dd57npk">
                      {card.value}
                    </p>
                    <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-137" data-mp="3n8vpnikshor6">{card.detail}</p>
                  </article>)}
              </div>

              {slide.velocityChips && <div className="mt-6 flex flex-wrap gap-3 font-mono text-sm leading-relaxed sm:text-base" data-id="element-138" data-mp="3e7xmt3m37na6">
                  {slide.velocityChips.map(chip => <span key={chip.prefix} className="rounded-2xl border border-slate-700 bg-panel2 px-4 py-2.5 text-slate-400" data-id="element-139" data-mp="1xw0o78tuhd6y">
                      <span className="font-semibold text-[#56d4a2]" data-id="element-140" data-mp="1v99ew25q4onw">{chip.prefix}</span>{chip.suffix}
                    </span>)}
                </div>}

              {slide.velocityFooter && <footer className="mt-8 flex flex-col gap-3 border-t border-slate-700 pt-5 font-mono text-sm sm:flex-row sm:items-center sm:justify-between sm:text-base" data-id="element-141" data-mp="2494vwnlyndvu">
                  <span className="font-semibold text-[#f5bc45]" data-id="element-142" data-mp="1bl8xnegqtdwy">OpenFPGA.ai</span>
                  <span className="text-slate-400" data-id="element-143" data-mp="17jx8fa1s6upa">{slide.velocityFooter}</span>
                </footer>}
            </motion.section>}

          {slide.competitiveTableRows && <motion.div variants={item} className="mt-7" aria-label="Competitive positioning" data-id="element-144" data-mp="1gklnm82wv13r">
              <div className="overflow-x-auto rounded-xl border border-white/[0.08]" data-id="element-145" data-mp="sv10re3mzjp7">
                <table className="w-full min-w-[760px] border-collapse text-left" data-id="element-146" data-mp="sso9ubfwhz00">
                  <thead className="bg-panel" data-id="element-147" data-mp="nvo6476ix8c2">
                    <tr className="border-b border-white/[0.08]" data-id="element-148" data-mp="2nypch7uvrcmc">
                      {['Approach', 'Decode efficiency', 'Deterministic latency', 'New model onto the chip'].map(heading => <th key={heading} scope="col" className="px-4 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400" data-id="element-149" data-mp="2xnh053swrarg">
                          {heading}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06] bg-panel2" data-id="element-150" data-mp="2ewzl22100dok">
                    {slide.competitiveTableRows.map(row => <tr key={row.approach} className={row.approach === 'OpenFPGA' ? 'bg-accent/[0.06]' : ''} data-id="element-151" data-mp="2j0nruiht358r">
                        <td className="px-4 py-3 align-top" data-id="element-152" data-mp="3qcedqtm6cjdv">
                          <div className={`text-sm font-semibold ${row.approach === 'OpenFPGA' ? 'text-accent' : 'text-white'}`} data-id="element-153" data-mp="3ud84dur04cag">{row.approach}</div>
                          <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-154" data-mp="1isqtailfjx0v">{row.approachDetail}</div>
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-155" data-mp="19xsxn3qwe65u">
                          <div className="text-sm font-medium text-slate-200" data-id="element-156" data-mp="19w9hre95lx4l">{row.decodeEfficiency}</div>
                          {row.decodeDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-157" data-mp="100xzwn2g89sm">{row.decodeDetail}</div>}
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-158" data-mp="uzs6en0ysii1">
                          <div className="text-sm font-medium text-slate-200" data-id="element-159" data-mp="qz89nkkazgwf">{row.deterministicLatency}</div>
                          {row.deterministicDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-160" data-mp="m284dbva2i6d">{row.deterministicDetail}</div>}
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-161" data-mp="2gexxhrdtnncp">
                          <div className="text-sm font-medium text-slate-200" data-id="element-162" data-mp="2gffzz5r2uxt2">{row.modelChange}</div>
                          {row.modelChangeDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-163" data-mp="1i2vc1e4e8aak">{row.modelChangeDetail}</div>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-[1.05fr_0.95fr]" data-id="element-164" data-mp="190m50y8c2z1x">
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-300" data-id="element-165" data-mp="uc82ypiqremf">{slide.footer}</p>
                <section className="rounded-xl border border-accent/20 bg-accent/[0.06] p-4" aria-labelledby="efficiency-heading" data-id="element-166" data-mp="z4rhbop96dsa">
                  <h2 id="efficiency-heading" className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-accent" data-id="element-167" data-mp="lai26t2yz3bz">Why the efficiency holds</h2>
                  <ul className="mt-3 space-y-1.5 text-sm leading-relaxed text-slate-300" data-id="element-168" data-mp="bfgcnw4s7z8v">
                    {slide.efficiencyPoints?.map(point => <li key={point} data-id="element-169" data-mp="gfktxodiky05">{point}</li>)}
                  </ul>
                </section>
              </div>

              {slide.proof && <p className="mt-3 border-l-2 border-accent pl-4 text-sm leading-relaxed text-slate-300" data-id="element-170" data-mp="3nysu2jh6f9w1">
                  <span className="font-semibold text-white" data-id="element-171" data-mp="6qxeoasfcq6t">OpenFPGA.ai</span>{' '}{slide.proof}
                </p>}
            </motion.div>}

          {slide.comparisonRows && <motion.div variants={item} className="mt-8 overflow-x-auto rounded-xl border border-white/[0.08]" data-id="element-172" data-mp="fpiva0ec5v4y">
              <table className="w-full min-w-[640px] border-collapse text-left" aria-label="GPU-serving pain points and FPGA answers" data-id="element-173" data-mp="pgds1yp7kd4n">
                <thead className="bg-panel" data-id="element-174" data-mp="kh0xers3i1ri">
                  <tr className="border-b border-white/[0.08]" data-id="element-175" data-mp="kjdk1i8st0aw">
                    <th scope="col" className="w-1/2 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400" data-id="element-176" data-mp="yhv5187bjyp4">
                      GPU-serving pain
                    </th>
                    <th scope="col" className="w-1/2 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-accent" data-id="element-177" data-mp="11jzprkk7maxm">
                      Our FPGA answer
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] bg-panel2" data-id="element-178" data-mp="1g7kpd4hvls0s">
                  {slide.comparisonRows.map(row => <tr key={row.pain} data-id="element-179" data-mp="1k84lq5umkrlv">
                      <td className="px-5 py-3.5 text-sm leading-relaxed text-slate-300" data-id="element-180" data-mp="1kb8yhrm5ovo5">
                        {row.pain}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-medium leading-relaxed text-accent/90" data-id="element-181" data-mp="1u72jlzdxxxhj">
                        {row.answer}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </motion.div>}

          {slide.footer && !slide.moatStages && !slide.competitiveTableRows && <motion.p variants={item} className="mt-6 border-t border-white/[0.08] pt-4 font-mono text-[11px] leading-relaxed text-slate-500" data-id="element-182" data-mp="2i2fbp911hj68">
              {slide.footer}
            </motion.p>}
        </div>

        {slide.portrait && <motion.figure variants={item} className="mx-auto mt-2 w-full max-w-[270px] lg:mx-0" data-id="element-183" data-mp="24b974yiwkvch">
            <div className="overflow-hidden rounded-xl border border-accent/25 bg-panel2 p-1.5 shadow-2xl shadow-black/30" data-id="element-184" data-mp="311jieb53z0uq">
              <img src={slide.portrait.src} alt={slide.portrait.alt} className="aspect-[4/5] w-full rounded-lg object-cover object-center" data-id="element-185" data-mp="2wzeu7rxfiwfh" />
            </div>
            <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-slate-500" data-id="element-186" data-mp="2n0parupvx3x4">
              Orion Parrott · Founder
            </figcaption>
          </motion.figure>}
      </div>
    </motion.div>;
}